import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IMenuItem, IPopupMenuConfig } from "../popup-menu/popup-menu.component";
import { ISortOutput, SortDirection } from "libraries/interfaces/ISort";
import { ILabels } from "libraries/interfaces/ILabels";
import { Yii } from "libraries/interfaces/Yii";
import { LabelService } from "libraries/service/label/label.service";
import { SimpleStorageService } from "libraries/service/simple-storage/simple-storage.service";
import { BtnStyleTypes } from "libraries/ui_button/btn.component";
import { faCaretDown, faCaretUp, IconDefinition } from "@fortawesome/free-solid-svg-icons";

export interface ISorterWording {
    [SortDirection.descending]: string;
    [SortDirection.ascending]: string;
}

@Component({
    selector: 'sorter',
    template: `
        <div class="sorter-container">
            <popup-menu [menuConfig]="menuConfig"
                        [selectedActionName]="selectedActionName"
                        [disabled]="disabled"
                        (onSelect)="selectAction($event)" class="e2e-menu-sorter"></popup-menu>
            <btn [label]="showWording ? wording[sortDirections.ascending] : null"
                 [type]="sortDirection === sortDirections.ascending && selectedActionName ? toggleTypes.on : toggleTypes.off"
                 (onClick)="toggleOnOff(sortDirections.ascending)"
                 class="e2e-list-up"
                 [disableIf]="!selectedActionName || disabled">
                <fa-icon *ngIf="showIcons" [icon]="iconUp" class="icon"></fa-icon>
            </btn>
            <btn [label]="showWording ? wording[sortDirections.descending] : null"
                 [type]="sortDirection === sortDirections.descending && selectedActionName ? toggleTypes.on : toggleTypes.off"
                 (onClick)="toggleOnOff(sortDirections.descending)"
                 [disableIf]="!selectedActionName || disabled" class="e2e-list-down">
                <fa-icon *ngIf="showIcons" [icon]="iconDown" class="icon"></fa-icon>
            </btn>
        </div>
    `,
    styles: [`
        .sorter-container {
            display: flex;
        }

        btn, popup-menu {
            margin-right: 5px;
        }

        :host {
            display: inline-block;
        }
    `]
})

export class SorterComponent implements OnInit {

    @Input()
    public menuConfig: IPopupMenuConfig;

    @Input()
    public sortStorageKey: string;

    @Input()
    public selectedActionName: string;

    @Input()
    public storageCategory: string;

    @Input()
    public sortDirection: SortDirection;

    @Input()
    public showWording: boolean = true;
    @Input()
    public showIcons: boolean = false;
    @Input()
    public disabled: boolean = false;

    @Input()
    public wording: ISorterWording = {
        [SortDirection.descending]: "Z - A",
        [SortDirection.ascending]: "A - Z",
    };

    @Input()
    public icons: ISorterWording = {
        [SortDirection.descending]: "fa-caret-down",
        [SortDirection.ascending]: "fa-caret-up",
    };

    public iconUp: IconDefinition = faCaretUp;
    public iconDown: IconDefinition = faCaretDown;

    @Output()
    public readonly onSort: EventEmitter<ISortOutput> = new EventEmitter<ISortOutput>();

    public sortDirections: typeof SortDirection = SortDirection;


    public toggleTypes: { [key: string]: BtnStyleTypes } = {
        on: BtnStyleTypes.action,
        off: BtnStyleTypes.action2,
    };

    constructor(
        protected readonly labelService: LabelService,
        protected readonly simpleStorageService: SimpleStorageService
    ) {}

    public ngOnInit(): void {
        this.restoreState();
        this.updatePopupMenu();
        this.onSort.emit({
            actionName: this.selectedActionName,
            direction: this.sortDirection
        });
    }

    public selectAction(actionName: string): void {
        this.selectedActionName = actionName;
        this.updatePopupMenu();
        this.saveState();
        this.onSort.emit(
            {
                actionName,
                direction: this.sortDirection
            }
        );
    }

    public toggleOnOff(direction: SortDirection): void {
        this.sortDirection = direction;
        this.saveState();
        this.onSort.emit(
            {
                actionName: this.selectedActionName,
                direction
            }
        );
    }

    private updatePopupMenu(): void {
        const selectedLabel: string = this.menuConfig.items
            .find((item: IMenuItem) => item.actionName === this.selectedActionName)
            .label;

        this.menuConfig.buttonLabel = this.labelService.completeLabel(
            Yii.t('wui-shared-js', 'Trié par : %placeholder%'),
            selectedLabel,
        );
    }

    private saveState(): void {
        const curState: ISortOutput = {direction: this.sortDirection, actionName: this.selectedActionName};
        this.simpleStorageService.store(this.storageCategory, this.sortStorageKey, curState);
    }

    private restoreState(): boolean {
        const savedState: ISortOutput = this.simpleStorageService.retrieve(this.storageCategory, this.sortStorageKey);
        if (savedState) {
            this.selectedActionName = savedState.actionName;
            this.sortDirection = savedState.direction;
        }
        return !!savedState;
    }
}
